import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import type { SentRequest } from '../../../../utils/requestHistory';
import { formatRequestTime, formatRequestDateTime } from '../../../../utils/requestHistory';
import type { AdminSentRequest } from '../../../../utils/adminRequestHistory';
import { formatJson } from '../../../../utils/formatJson';
import Icon, { ICONS } from '../../icons';
import TracingTab from './TracingTab';
import { useEscapeKey } from '../../../../hooks/useEscapeKey';

interface DataPopupTabLabels {
  current?: string;
  history?: string;
  tracing?: string;
  copy?: string;
  copied?: string;
  noHistory?: string;
  allUsers?: string;
  allUsersEmpty?: string;
  allUsersError?: string;
  allUsersLoading?: string;
}

type DataPopupTab = 'current' | 'history' | 'tracing' | 'allUsers';
type AllUsersStatus = 'idle' | 'loading' | 'loaded' | 'error';

interface ResendResult {
  loading?: boolean;
  response?: any;
  httpStatus?: number;
  error?: string;
  duration_ms?: number;
  responseMode?: string;
}

interface DataPopupProps {
  isOpen: boolean;
  title: string;
  data: any;
  history?: SentRequest[];
  stepHistory?: any[];
  tracingLocalization?: any;
  tabLabels?: DataPopupTabLabels;
  onResendStep?: (stepIndex: number, responseMode: 'streaming' | 'blocking') => void;
  resendResults?: Record<number, ResendResult>;
  /** Global-admin only: lazily loads other users' persisted requests for the "All users" tab. */
  adminHistoryLoader?: () => Promise<AdminSentRequest[]>;
  onClose: () => void;
}

// Single place that turns a recorded request into its display payload:
// strips admin-hidden fields and re-adds non-empty URL buckets.
const buildHistoryPayload = (request: SentRequest) => {
  const inputs = request.inputs || {};
  // _hidden_fields is backend metadata listing admin-configured hidden field IDs.
  // Strip both the metadata key and the hidden field values from the display payload.
  const hiddenFieldIds = Array.isArray(inputs._hidden_fields)
    ? new Set(inputs._hidden_fields.map(String))
    : new Set<string>();

  const payload: Record<string, any> = {};
  for (const [key, value] of Object.entries(inputs)) {
    if (key === '_hidden_fields') continue;
    if (hiddenFieldIds.has(key)) continue;
    payload[key] = value;
  }

  // Add categorized URLs if they have content
  if (request.categorizedUrls) {
    if (request.categorizedUrls.image_urls?.length > 0) {
      payload.image_urls = request.categorizedUrls.image_urls;
    }
    if (request.categorizedUrls.video_urls?.length > 0) {
      payload.video_urls = request.categorizedUrls.video_urls;
    }
    if (request.categorizedUrls.audio_urls?.length > 0) {
      payload.audio_urls = request.categorizedUrls.audio_urls;
    }
  }

  return payload;
};

// Copy shape shared by the History and All users tabs.
const toHistoryCopyShape = (requests: SentRequest[]) =>
  requests.map(request => ({
    timestamp: request.timestamp,
    workflowId: request.workflowId,
    workflowTitle: request.workflowTitle,
    payload: buildHistoryPayload(request),
  }));

const statusBadgeClass = (status: string | null) => {
  switch (status) {
    case 'success':
      return 'bg-green-900/50 text-green-300';
    case 'failed':
      return 'bg-red-900/50 text-red-300';
    default:
      return 'bg-yellow-900/50 text-yellow-300';
  }
};

interface HistoryRequestListProps<T extends SentRequest> {
  items: T[];
  expandedIndex: number | null;
  onToggle: (index: number) => void;
  emptyText: string;
  renderHeaderLabel: (item: T) => React.ReactNode;
}

function HistoryRequestList<T extends SentRequest>({ items, expandedIndex, onToggle, emptyText, renderHeaderLabel }: HistoryRequestListProps<T>) {
  return (
    <div className="space-y-3">
      {items.length === 0 ? (
        <div className="text-center text-gray-500 py-8">
          {emptyText}
        </div>
      ) : (
        items.map((request, index) => (
          <div
            key={index}
            className="bg-gray-800 border border-gray-600 rounded overflow-hidden"
          >
            {/* History item header */}
            <button
              onClick={() => onToggle(index)}
              className="w-full px-4 py-3 flex items-center justify-between text-left hover:bg-gray-750 transition-colors"
            >
              <div className="flex items-center gap-3">
                {renderHeaderLabel(request)}
                {request.categorizedUrls && (
                  <div className="flex gap-1">
                    {request.categorizedUrls.image_urls?.length > 0 && (
                      <span className="px-1.5 py-0.5 text-xs bg-blue-900/50 text-blue-300 rounded">
                        {request.categorizedUrls.image_urls.length} img
                      </span>
                    )}
                    {request.categorizedUrls.video_urls?.length > 0 && (
                      <span className="px-1.5 py-0.5 text-xs bg-green-900/50 text-green-300 rounded">
                        {request.categorizedUrls.video_urls.length} vid
                      </span>
                    )}
                    {request.categorizedUrls.audio_urls?.length > 0 && (
                      <span className="px-1.5 py-0.5 text-xs bg-yellow-900/50 text-yellow-300 rounded">
                        {request.categorizedUrls.audio_urls.length} aud
                      </span>
                    )}
                  </div>
                )}
              </div>
              <span className="text-gray-400 text-lg">
                {expandedIndex === index ? '▼' : '▶'}
              </span>
            </button>

            {/* History item content (expanded) */}
            {expandedIndex === index && (
              <div className="border-t border-gray-700 p-4">
                <pre className="text-sm text-gray-100 overflow-auto whitespace-pre-wrap">
                  {formatJson(buildHistoryPayload(request))}
                </pre>
              </div>
            )}
          </div>
        ))
      )}
    </div>
  );
}

const DataPopup: React.FC<DataPopupProps> = ({ isOpen, title, data, history = [], stepHistory, tracingLocalization, tabLabels, onResendStep, resendResults, adminHistoryLoader, onClose }) => {
  useEscapeKey(onClose, isOpen);
  const [activeTab, setActiveTab] = useState<DataPopupTab>('current');
  const [expandedHistoryIndex, setExpandedHistoryIndex] = useState<number | null>(null);
  const [copied, setCopied] = useState(false);
  const [allUsersStatus, setAllUsersStatus] = useState<AllUsersStatus>('idle');
  const [allUsersItems, setAllUsersItems] = useState<AdminSentRequest[]>([]);
  const [allUsersExpandedIndex, setAllUsersExpandedIndex] = useState<number | null>(null);
  // Bumped on every open/close so a load started in an earlier session is ignored.
  const allUsersRequestIdRef = useRef(0);

  // Reset to appropriate default tab when popup opens with new content
  useEffect(() => {
    if (isOpen) {
      const hasTracing = stepHistory && stepHistory.length > 0;
      setActiveTab(hasTracing ? 'tracing' : 'current');
      setExpandedHistoryIndex(null);
      setCopied(false);
    }
  }, [isOpen, stepHistory]);

  // All users state lives for one popup session and one workflow: reopening, or a new
  // loader (the selected workflow changed while open), starts from idle (and retries).
  useEffect(() => {
    allUsersRequestIdRef.current += 1;
    setAllUsersStatus('idle');
    setAllUsersItems([]);
    setAllUsersExpandedIndex(null);
    // Loading starts only from a tab click, so never leave the tab showing an idle view.
    setActiveTab(tab => (tab === 'allUsers' ? 'current' : tab));
  }, [isOpen, adminHistoryLoader]);

  if (!isOpen) return null;

  const copyToClipboard = async () => {
    try {
      let textToCopy: string;
      if (activeTab === 'current') {
        textToCopy = formatJson(data);
      } else if (activeTab === 'tracing') {
        textToCopy = formatJson(stepHistory);
      } else if (activeTab === 'allUsers') {
        textToCopy = formatJson(toHistoryCopyShape(allUsersItems));
      } else {
        textToCopy = formatJson(toHistoryCopyShape(history));
      }
      await navigator.clipboard.writeText(textToCopy);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy to clipboard:', err);
    }
  };

  const toggleHistoryItem = (index: number) => {
    setExpandedHistoryIndex(expandedHistoryIndex === index ? null : index);
  };

  const toggleAllUsersItem = (index: number) => {
    setAllUsersExpandedIndex(allUsersExpandedIndex === index ? null : index);
  };

  // The loader runs only from here, once per popup session and workflow.
  const openAllUsersTab = async () => {
    setActiveTab('allUsers');
    if (!adminHistoryLoader || allUsersStatus !== 'idle') return;

    const requestId = ++allUsersRequestIdRef.current;
    setAllUsersStatus('loading');
    try {
      const items = await adminHistoryLoader();
      if (requestId !== allUsersRequestIdRef.current) return;
      setAllUsersItems(items);
      setAllUsersStatus('loaded');
    } catch (err) {
      if (requestId !== allUsersRequestIdRef.current) return;
      console.error('Failed to load admin request history:', err);
      setAllUsersStatus('error');
    }
  };

  const hasTracing = stepHistory && stepHistory.length > 0;

  const renderAllUsersContent = () => {
    if (allUsersStatus === 'error') {
      return (
        <div className="text-center text-red-400 py-8">
          {tabLabels?.allUsersError || 'Failed to load request history. Reopen the preview to try again.'}
        </div>
      );
    }
    if (allUsersStatus !== 'loaded') {
      return (
        <div className="text-center text-gray-500 py-8">
          {tabLabels?.allUsersLoading || 'Loading request history...'}
        </div>
      );
    }
    return (
      <HistoryRequestList
        items={allUsersItems}
        expandedIndex={allUsersExpandedIndex}
        onToggle={toggleAllUsersItem}
        emptyText={tabLabels?.allUsersEmpty || 'No requests for this workflow yet.'}
        renderHeaderLabel={(request) => (
          <>
            <span className="text-gray-400 text-sm font-mono">
              {formatRequestDateTime(request.timestamp)}
            </span>
            <span className="text-white font-medium">
              {request.organizationName || '—'}
            </span>
            <span className="text-gray-300 text-sm">
              {request.email || '—'}
            </span>
            {request.status && (
              <span className={`px-1.5 py-0.5 text-xs rounded ${statusBadgeClass(request.status)}`}>
                {request.status}
              </span>
            )}
          </>
        )}
      />
    );
  };

  const modalContent = (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-gray-900 border-2 border-purple-500/30 rounded-lg shadow-2xl max-w-4xl w-full mx-4 max-h-[80vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-700">
          <h3 className="text-xl font-bold text-white flex items-center gap-2">
            <span className="text-2xl">📄</span>
            {title}
          </h3>
          <div className="flex items-center gap-2">
            <button
              onClick={copyToClipboard}
              className={`px-3 py-1 text-sm rounded transition-colors flex items-center gap-1 ${
                copied
                  ? 'bg-green-700/60 text-green-200'
                  : 'bg-blue-900/50 text-blue-200 hover:bg-blue-800/50'
              }`}
            >
              <Icon name={copied ? ICONS.CHECK : ICONS.COPY} size={14} className="text-current" />
              {copied ? (tabLabels?.copied || 'Copied!') : (tabLabels?.copy || 'Copy')}
            </button>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-white text-2xl leading-none p-1"
            >
              ✕
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-700">
          <button
            onClick={() => setActiveTab('current')}
            className={`px-6 py-3 text-sm font-medium transition-colors ${
              activeTab === 'current'
                ? 'text-purple-400 border-b-2 border-purple-400 bg-purple-900/20'
                : 'text-gray-400 hover:text-white hover:bg-gray-800'
            }`}
          >
            {tabLabels?.current || 'Current'}
          </button>
          <button
            onClick={() => setActiveTab('history')}
            className={`px-6 py-3 text-sm font-medium transition-colors flex items-center gap-2 ${
              activeTab === 'history'
                ? 'text-purple-400 border-b-2 border-purple-400 bg-purple-900/20'
                : 'text-gray-400 hover:text-white hover:bg-gray-800'
            }`}
          >
            {tabLabels?.history || 'History'}
            {history.length > 0 && (
              <span className="px-1.5 py-0.5 text-xs bg-gray-700 rounded-full">
                {history.length}
              </span>
            )}
          </button>
          {hasTracing && (
            <button
              onClick={() => setActiveTab('tracing')}
              className={`px-6 py-3 text-sm font-medium transition-colors flex items-center gap-2 ${
                activeTab === 'tracing'
                  ? 'text-purple-400 border-b-2 border-purple-400 bg-purple-900/20'
                  : 'text-gray-400 hover:text-white hover:bg-gray-800'
              }`}
              data-testid="tracing-tab-button"
            >
              {tabLabels?.tracing || 'Tracing'}
              <span className="px-1.5 py-0.5 text-xs bg-gray-700 rounded-full">
                {stepHistory!.length}
              </span>
            </button>
          )}
          {adminHistoryLoader && (
            <button
              onClick={openAllUsersTab}
              className={`px-6 py-3 text-sm font-medium transition-colors flex items-center gap-2 ${
                activeTab === 'allUsers'
                  ? 'text-purple-400 border-b-2 border-purple-400 bg-purple-900/20'
                  : 'text-gray-400 hover:text-white hover:bg-gray-800'
              }`}
              data-testid="all-users-tab-button"
            >
              {tabLabels?.allUsers || 'All users'}
              {allUsersStatus === 'loaded' && (
                <span className="px-1.5 py-0.5 text-xs bg-gray-700 rounded-full">
                  {allUsersItems.length}
                </span>
              )}
            </button>
          )}
        </div>

        {/* Content */}
        <div className="p-6 overflow-auto max-h-[calc(80vh-180px)]">
          {activeTab === 'current' ? (
            <pre className="bg-gray-800 border border-gray-600 rounded p-4 text-sm text-gray-100 overflow-auto whitespace-pre-wrap">
              {formatJson(data)}
            </pre>
          ) : activeTab === 'tracing' && hasTracing ? (
            <TracingTab stepHistory={stepHistory!} localization={tracingLocalization} onResendStep={onResendStep} resendResults={resendResults} />
          ) : activeTab === 'allUsers' ? (
            renderAllUsersContent()
          ) : (
            <HistoryRequestList
              items={history}
              expandedIndex={expandedHistoryIndex}
              onToggle={toggleHistoryItem}
              emptyText={tabLabels?.noHistory || 'No request history yet. History will appear after you send requests.'}
              renderHeaderLabel={(request) => (
                <>
                  <span className="text-gray-400 text-sm font-mono">
                    {formatRequestTime(request.timestamp)}
                  </span>
                  <span className="text-white font-medium">
                    {request.workflowTitle}
                  </span>
                </>
              )}
            />
          )}
        </div>
      </div>
    </div>
  );

  // Use portal to render modal at document body level
  return createPortal(modalContent, document.body);
};

export default DataPopup;
